import React, {Component, ReactNode, ErrorInfo} from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
} from 'react-native';
import {Logger} from './logger';

interface Props {
  children: ReactNode;
  /** Optional fallback shown in release instead of crash screen */
  releaseFallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {hasError: false, error: null, errorInfo: null};

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({errorInfo: info});

    // Log detailed error information
    Logger.error('ErrorBoundary', `${error.name}: ${error.message}`, error, {
      componentStack: info.componentStack,
      errorType: this.getErrorType(error),
      timestamp: new Date().toISOString(),
    });

    // Show user-friendly message based on error type
    const userMessage = this.getErrorMessage(error);
    if (userMessage) {
      Logger.warn('ErrorBoundary', userMessage);
    }
  }

  private getErrorType(error: Error): string {
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return 'NetworkError';
    }
    if (error.message.includes('Permission')) {
      return 'PermissionError';
    }
    if (error.message.includes('undefined')) {
      return 'NullReferenceError';
    }
    return error.name || 'UnknownError';
  }

  private getErrorMessage(error: Error): string {
    if (error.message.includes('Network')) {
      return 'Network connection issue. Please check your internet.';
    }
    if (error.message.includes('Permission')) {
      return 'Missing required permissions. Please check app settings.';
    }
    if (error.message.includes('undefined') || error.message.includes('cannot read')) {
      return 'App encountered a data issue. Please restart and try again.';
    }
    return '';
  }

  reset = () => this.setState({hasError: false, error: null, errorInfo: null});

  render() {
    if (!this.state.hasError) return this.props.children;

    // ── Release build: show a friendly screen, no internals ──
    if (!__DEV__) {
      return (
        this.props.releaseFallback ?? (
          <SafeAreaView style={styles.releaseContainer}>
            <Text style={styles.releaseIcon}>⚠️</Text>
            <Text style={styles.releaseTitle}>Something went wrong</Text>
            <Text style={styles.releaseBody}>
              Please restart the app. If the problem persists, contact support.
            </Text>
            <TouchableOpacity style={styles.releaseBtn} onPress={this.reset}>
              <Text style={styles.releaseBtnText}>Try Again</Text>
            </TouchableOpacity>
          </SafeAreaView>
        )
      );
    }

    // ── Dev build: full crash details ──
    const {error, errorInfo} = this.state;
    const errorType = this.getErrorType(error || new Error());
    const userMessage = this.getErrorMessage(error || new Error());

    return (
      <SafeAreaView style={styles.devContainer}>
        <View style={styles.devHeader}>
          <Text style={styles.devHeaderText}>🔴  Render Error (DEV)</Text>
          <TouchableOpacity onPress={this.reset} style={styles.retryBtn}>
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <View style={styles.errorTypeBox}>
            <Text style={styles.errorType}>{errorType}</Text>
          </View>

          <Text style={styles.errorName}>{error?.name}</Text>
          <Text style={styles.errorMessage}>{error?.message}</Text>

          {userMessage && (
            <View style={styles.userMessageBox}>
              <Text style={styles.userMessage}>💡 User Message: {userMessage}</Text>
            </View>
          )}

          {error?.stack && (
            <>
              <Text style={styles.sectionLabel}>JS Stack</Text>
              <ScrollView horizontal>
                <Text style={styles.codeBlock}>{error.stack}</Text>
              </ScrollView>
            </>
          )}

          {errorInfo?.componentStack && (
            <>
              <Text style={styles.sectionLabel}>Component Stack</Text>
              <ScrollView horizontal>
                <Text style={styles.codeBlock}>{errorInfo.componentStack}</Text>
              </ScrollView>
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  // Release
  releaseContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    backgroundColor: '#f5f7fa',
  },
  releaseIcon: {fontSize: 52, marginBottom: 16},
  releaseTitle: {fontSize: 20, fontWeight: '700', color: '#1a1a2e', marginBottom: 8},
  releaseBody: {fontSize: 14, color: '#6b7280', textAlign: 'center', lineHeight: 22},
  releaseBtn: {
    marginTop: 24,
    backgroundColor: '#6C3CE1',
    paddingHorizontal: 28,
    paddingVertical: 12,
    borderRadius: 10,
  },
  releaseBtnText: {color: '#fff', fontWeight: '700', fontSize: 15},

  // Dev
  devContainer: {flex: 1, backgroundColor: '#1a1a2e'},
  devHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#e74c3c',
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  devHeaderText: {color: '#fff', fontWeight: '700', fontSize: 14},
  retryBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 6,
  },
  retryText: {color: '#fff', fontSize: 13, fontWeight: '600'},
  scroll: {flex: 1},
  scrollContent: {padding: 16},
  errorTypeBox: {
    backgroundColor: '#3b1212',
    borderLeftColor: '#f87171',
    borderLeftWidth: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
    marginBottom: 12,
  },
  errorType: {fontSize: 12, fontWeight: '700', color: '#f87171', textTransform: 'uppercase'},
  errorName: {fontSize: 16, fontWeight: '800', color: '#ff6b6b', marginBottom: 4},
  errorMessage: {fontSize: 14, color: '#ffd93d', marginBottom: 16, lineHeight: 20},
  userMessageBox: {
    backgroundColor: '#1e3a5f',
    borderLeftColor: '#60a5fa',
    borderLeftWidth: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    marginBottom: 16,
  },
  userMessage: {fontSize: 13, color: '#93c5fd', lineHeight: 18},
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
    marginTop: 12,
  },
  codeBlock: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: '#a8d8a8',
    lineHeight: 18,
    backgroundColor: '#0d1117',
    padding: 10,
    borderRadius: 6,
  },
});
